/**
 * Helper — Fonctions 4ᵉ (dépendance de deux grandeurs, attendus 175-177).
 *
 * - Arithmétique DÉCIMALE EXACTE en dixièmes entiers (aucun flottant) : tous les
 *   tarifs (part fixe F, prix variable v, tarif simple w) sont en dixièmes, les
 *   quantités n sont entières → prix = somme/produit de dixièmes, exact.
 * - `tirerTarif` (175, 176) : situation « deux tarifs » (simple proportionnel
 *   w×n vs abonnement affine F + v×n) sur contextes variés, avec un CROISEMENT
 *   à un entier n0 par construction (F = (w − v) × n0). Option `entier` : F, v,
 *   w en euros entiers → points à coordonnées entières pour placer_points_2d.
 * - `svgCourbe` (177 seul) : courbe de température linéaire par morceaux,
 *   quadrillage tous les 0,5 °C, graduations numérotées aux degrés entiers,
 *   titres d'axes « Température (en °C) » et « Heure (en h) », aucun point
 *   marqué sur la courbe (revue 2026-09-23 : les points rouges montraient où
 *   lire, « °C » tombait sous la pointe de la flèche).
 * - Profils de courbe curés (177) en DEMI-DEGRÉS : toute température lue tombe
 *   sur une ligne du quadrillage, par construction (revue 2026-09-23 : avant,
 *   80,4 % des valeurs demandées étaient entre deux lignes). Les SIMPLES
 *   montent strictement jusqu'à un pic unique puis descendent strictement, à
 *   des pentes différentes ; les PALIERS ont un seul intervalle de 2 h où la
 *   température ne change pas, jamais au sommet ; les CREUX commencent par
 *   baisser jusqu'à 8 h ou 10 h (réservés au QCM de croissance et au QCM
 *   « pour la première fois »). Leurs propriétés (valeurs atteintes deux fois,
 *   paire leurre symétrique, grille « première fois ») sont recalculées et
 *   contrôlées par gen_177 à chaque tirage.
 * Revue 2026-09-23 : phrases fausses retirées de cet en-tête (aucun solveur ne
 * relit la courbe, le bloc <desc> qu'on lui destinait est supprimé ; le palier
 * durait 2 h et non 4 h ; le tirage n'était pas borné à 36,0 et 40,5 °C).
 * Vague 7 (2026-09-25, correction de 176) : `svgAireRestante` + `ALT_AIRE_RESTANTE`,
 * la figure du plafond (rectangle, quatre carrés gris aux coins, ni cote ni
 * lettre), copiée du `svgAire` privé de gen_175 pour être partagée ; gen_175
 * garde sa copie privée (fichier hors du périmètre de la vague), à basculer sur
 * celle-ci. `SACRIFIE_TARIF` : le couple (F ; v) = (20 ; 2) de la revue de 175,
 * que 176 exclut de ses tirages.
 */

// ═══════════════════════════════════════════════════════════
// Décimaux exacts en dixièmes
// ═══════════════════════════════════════════════════════════

/** Dixièmes entiers → chaîne française ("35" → "3,5", "40" → "4"). */
export const fmtT = (t) => {
    const neg = t < 0 ? '-' : '';
    const a = Math.abs(t);
    const int = Math.trunc(a / 10);
    const dec = a % 10;
    return dec === 0 ? `${neg}${int}` : `${neg}${int},${dec}`;
};

// ═══════════════════════════════════════════════════════════
// Tableaux d'énoncé + image
// ═══════════════════════════════════════════════════════════

const T_TH = 'background:#1E3A8A;color:#fff;padding:6px 14px;border:1px solid #64748b;font-weight:600';
const T_TD = 'padding:6px 14px;border:1px solid #64748b;text-align:center';
export const ligneTable = (th, cells) => `<tr><th style="${T_TH}">${th}</th>`
    + cells.map((c) => `<td style="${T_TD}">${c}</td>`).join('') + '</tr>';
export const tableEnonce = (...lignes) =>
    `<table style="border-collapse:collapse;margin:12px auto">${lignes.join('')}</table>`;

/** Élément additionnel image (canal normatif §12 FORMATS_TANOED) ; `alt` facultatif (décrit la figure, jamais la réponse). */
export const imgElement = (svg, maxWidth = 'medium', alt = '') => ({
    version: 1,
    elements: [{ type: 'image', config: {
        imageUrl: `data:image/svg+xml,${encodeURIComponent(svg)}`, maxWidth,
        ...(alt ? { alt } : {}),
    } }],
});

// ═══════════════════════════════════════════════════════════
// La figure du plafond (175, 176) : l'aire restante d'un rectangle
// ═══════════════════════════════════════════════════════════

/** Alt de la figure du plafond : décrit la figure, jamais une valeur. */
export const ALT_AIRE_RESTANTE = 'Un rectangle dont on a enlevé, à chacun des quatre coins, un carré gris ; les quatre carrés ont la même taille ; la figure restante est en blanc.';

/**
 * La figure du plafond (Eduscol, attendus de fin de 4e, « Comprendre et utiliser
 * la notion de fonction ») : le rectangle aux proportions L × l, les quatre
 * carrés gris aux coins, la figure restante en blanc ; ni cote ni lettre (le
 * texte donne les dimensions, la lettre x est dans l'énoncé). Chaîne SVG, à
 * passer à `imgElement`. Même dessin que le `svgAire` de gen_175.
 */
export const svgAireRestante = (L, l) => {
    const W = 220;
    const H = Math.round((W * l) / L);                 // l ≥ L/2 : de 110 à 209
    const c = Math.round(H * 0.24);                    // côté dessiné d'un carré
    const m = 10;
    const x0 = m; const y0 = m;
    const x1 = x0 + W; const y1 = y0 + H;
    const reste = [
        [x0 + c, y0], [x1 - c, y0], [x1 - c, y0 + c], [x1, y0 + c], [x1, y1 - c], [x1 - c, y1 - c],
        [x1 - c, y1], [x0 + c, y1], [x0 + c, y1 - c], [x0, y1 - c], [x0, y0 + c], [x0 + c, y0 + c],
    ].map((p) => p.join(',')).join(' ');
    const carres = [[x0, y0], [x1 - c, y0], [x0, y1 - c], [x1 - c, y1 - c]]
        .map(([x, y]) => `<rect x="${x}" y="${y}" width="${c}" height="${c}" fill="#cbd5e1" stroke="#64748b" stroke-width="1"/>`)
        .join('');
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W + 2 * m} ${H + 2 * m}" width="${W + 2 * m}" height="${H + 2 * m}">`
        + carres
        + `<polygon points="${reste}" fill="#ffffff" stroke="#1E3A8A" stroke-width="2" stroke-linejoin="round"/>`
        + '</svg>';
};

// ═══════════════════════════════════════════════════════════
// Situation « deux tarifs » (O12 / O13)
// ═══════════════════════════════════════════════════════════

/** (F ; v) = (20 ; 2) : sacrifié à la revue de 175 (2026-09-23), exclu des tirages de 176. */
export const SACRIFIE_TARIF = { F: 20, v: 2 };

export const CONTEXTES = [
    { lieu: 'la piscine', act: 'entrée', actPl: 'entrées', simple: 'entrée simple', abo: 'un abonnement annuel' },
    { lieu: 'la salle d’escalade', act: 'séance', actPl: 'séances', simple: 'séance à l’unité', abo: 'un abonnement au mois' },
    { lieu: 'le cinéma', act: 'place', actPl: 'places', simple: 'place au tarif normal', abo: 'une carte d’abonné' },
    { lieu: 'la patinoire', act: 'entrée', actPl: 'entrées', simple: 'entrée à l’unité', abo: 'un abonnement à l’année' },
    { lieu: 'le parc aquatique', act: 'entrée', actPl: 'entrées', simple: 'entrée à l’unité', abo: 'un pass saison' },
    { lieu: 'le club de tennis', act: 'partie', actPl: 'parties', simple: 'partie à l’unité', abo: 'un abonnement au trimestre' },
];

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

/** « le cinéma » → « au cinéma » ; « la piscine » → « à la piscine ». */
export const auLieu = (lieu, cap = false) => {
    let r;
    if (lieu.startsWith('le ')) r = `au ${lieu.slice(3)}`;
    else if (lieu.startsWith('la ')) r = `à la ${lieu.slice(3)}`;
    else if (lieu.startsWith('l’') || lieu.startsWith("l'")) r = `à l’${lieu.slice(2)}`;
    else r = `à ${lieu}`;
    return cap ? r.charAt(0).toUpperCase() + r.slice(1) : r;
};

/** Élision « de » : « de entrées » → « d’entrées » ; « de places » inchangé. */
export const deMot = (mot) => (/^[aeiouyéèêhàâî]/i.test(mot) ? `d’${mot}` : `de ${mot}`);

/**
 * Tire une situation « deux tarifs ».
 *  - w10 : prix simple (proportionnel) en dixièmes d'euro.
 *  - v10 : prix par unité avec abonnement (v < w) en dixièmes.
 *  - n0  : entrée où les deux tarifs s'ÉGALENT (entier) → avantageux dès n0+1.
 *  - F10 : part fixe de l'abonnement = (w10 − v10) × n0 (exact, en dixièmes).
 * @param {Object} o - { entier } : si true, w/v/F en euros ENTIERS (placer_2d).
 */
export const tirerTarif = (o = {}) => {
    const ctx = pick(CONTEXTES);
    let w10; let v10; let n0;
    if (o.entier) {
        // Euros PAIRS → tous les points du repère tombent sur des graduations de 2.
        v10 = 20;                           // 2 € par unité
        w10 = pick([40, 60]);               // 4 € ou 6 € à l'unité
        n0 = pick([6, 8, 10]);
    } else {
        v10 = pick([15, 20, 25, 30]);       // 1,5 à 3 €
        w10 = v10 + pick([10, 15, 20]);     // écart 1 à 2 €
        n0 = pick([8, 10, 12, 15, 20]);
    }
    const F10 = (w10 - v10) * n0;           // dixièmes exacts
    // Prix des deux tarifs pour n entrées, en dixièmes exacts.
    const prixSimple = (n) => w10 * n;      // proportionnel
    const prixAbo = (n) => F10 + v10 * n;   // affine
    return {
        ctx, w10, v10, F10, n0, prixSimple, prixAbo,
        fSimple: `${fmtT(w10)} × n`,
        fAbo: v10 === 0 ? `${fmtT(F10)}` : `${fmtT(F10)} + ${fmtT(v10)} × n`,
    };
};

// ═══════════════════════════════════════════════════════════
// Courbe de température (O14, gen_177)
// ═══════════════════════════════════════════════════════════

export const HOURS = [6, 8, 10, 12, 14, 16, 18, 20, 22];

// Profils en DEMI-DEGRÉS au-dessus de 36 °C : u vaut 36 + u/2 °C (u = 1 :
// 36,5 °C ; u = 9 : 40,5 °C). Un décalage entier s est ajouté au tirage.
// SIMPLES : montée stricte jusqu'au pic, descente stricte, pentes différentes ;
// six pics à 12 h, trois à 14 h (les seuls de ce pic qui portent à la fois une
// valeur atteinte deux fois non symétrique avec sa paire leurre et la grille
// « première fois »), six à 16 h. gen_177 tire d'abord l'heure du pic.
export const PROFILS_SIMPLES = [
    [1, 2, 3, 6, 5, 4, 3, 2, 1], // pic 12 h
    [1, 3, 4, 7, 6, 5, 4, 3, 2], // pic 12 h
    [1, 4, 5, 8, 7, 6, 5, 4, 3], // pic 12 h
    [1, 2, 4, 7, 6, 5, 4, 2, 1], // pic 12 h
    [1, 2, 5, 8, 7, 6, 5, 2, 1], // pic 12 h
    [1, 3, 4, 7, 6, 5, 4, 3, 1], // pic 12 h
    [1, 2, 3, 4, 7, 6, 5, 4, 3], // pic 14 h
    [1, 2, 4, 5, 8, 7, 6, 5, 4], // pic 14 h
    [1, 3, 4, 5, 8, 7, 6, 5, 4], // pic 14 h
    [1, 2, 3, 4, 5, 6, 3, 2, 1], // pic 16 h
    [1, 3, 4, 5, 6, 7, 4, 3, 2], // pic 16 h
    [1, 4, 5, 6, 7, 8, 5, 4, 3], // pic 16 h
    [1, 2, 4, 5, 6, 7, 4, 2, 1], // pic 16 h
    [1, 2, 5, 6, 7, 8, 5, 2, 1], // pic 16 h
    [1, 3, 4, 5, 6, 7, 4, 3, 1], // pic 16 h
];

// PALIERS : un seul intervalle de 2 h à température constante, jamais au
// sommet (le maximum reste atteint à une seule heure).
export const PROFILS_PALIER = [
    [1, 3, 5, 5, 7, 8, 6, 4, 3], // palier 10 h – 12 h, pic 16 h
    [1, 2, 4, 6, 6, 7, 5, 4, 2], // palier 12 h – 14 h, pic 16 h
    [1, 3, 5, 7, 6, 4, 4, 3, 2], // palier 16 h – 18 h, pic 12 h
    [2, 3, 5, 6, 7, 5, 3, 3, 2], // palier 18 h – 20 h, pic 14 h
    [1, 1, 3, 5, 7, 6, 5, 4, 3], // palier 6 h – 8 h, pic 14 h
    [1, 3, 4, 6, 8, 6, 5, 5, 3], // palier 18 h – 20 h, pic 14 h
    [1, 2, 4, 6, 7, 6, 5, 3, 3], // palier 20 h – 22 h, pic 14 h
    [1, 3, 3, 5, 7, 8, 6, 4, 2], // palier 8 h – 10 h, pic 16 h
];

// CREUX INITIAL (revue 2026-09-23, dernière correction) : la température
// commence par BAISSER jusqu'à un creux à 8 h ou 10 h, monte jusqu'au pic (14,
// 16 ou 18 h), puis redescend. Réservés aux deux QCM qui en ont besoin : la
// croissance (la montée ne part plus toujours de 6 h) et « pour la première
// fois » (la clé peut tomber à 14 ou 16 h). Les autres formats n'en veulent pas :
// au QCM multiple, une valeur atteinte au début de la baisse le serait trois
// fois. Chacun offre, pour « la première fois », une ligne voisine au-dessus
// pour une valeur et au-dessous pour une autre (contrôlé par gen_177).
export const PROFILS_CREUX = [
    [2, 1, 3, 4, 5, 4, 3, 2, 1], // creux 8 h, pic 14 h
    [2, 1, 3, 4, 6, 5, 4, 3, 2], // creux 8 h, pic 14 h
    [2, 1, 2, 3, 4, 5, 4, 3, 2], // creux 8 h, pic 16 h
    [2, 1, 2, 3, 4, 6, 5, 4, 3], // creux 8 h, pic 16 h
    [2, 1, 2, 3, 4, 5, 6, 5, 4], // creux 8 h, pic 18 h
    [2, 1, 2, 3, 4, 5, 7, 5, 4], // creux 8 h, pic 18 h
    [3, 2, 1, 4, 5, 6, 5, 4, 3], // creux 10 h, pic 16 h
    [3, 2, 1, 4, 5, 7, 6, 5, 4], // creux 10 h, pic 16 h
    [3, 2, 1, 2, 4, 5, 6, 5, 4], // creux 10 h, pic 18 h
    [3, 2, 1, 2, 5, 6, 7, 6, 5], // creux 10 h, pic 18 h
];

/** Un profil décalé de s demi-degrés, en dixièmes de °C (365 = 36,5 °C). */
export const enDixiemes = (profil, s) => profil.map((u) => 360 + 5 * (u + s));

/** Les décalages qui gardent la courbe entre 36,5 et 40,5 °C. */
export const decalagesPossibles = (profil) => {
    const out = [];
    for (let s = 1 - Math.min(...profil); Math.max(...profil) + s <= 9; s++) out.push(s);
    return out;
};

/** Description de la courbe, sans aucune valeur lue (alt de l'image). */
export const ALT_COURBE = 'Courbe de la température d’un patient, relevée de 6 h à 22 h : l’heure en abscisse, de 2 h en 2 h ; la température en ordonnée, en degrés Celsius, avec une ligne tous les demi-degrés.';

/**
 * Courbe de température : linéaire par morceaux sur les points (HOURS[i], temps[i])
 * (dixièmes de °C, multiples de 5). Quadrillage horizontal tous les 0,5 °C,
 * numéroté aux degrés entiers (la lecture d'une demi-graduation fait partie de
 * l'exercice, comme dans l'exemple d'Eduscol) ; quadrillage vertical à chaque
 * heure de relevé. Aucun point marqué sur la courbe.
 */
export const svgCourbe = (temps) => {
    const w = 440; const h = 290; const mL = 46; const mB = 46; const mT = 30; const mR = 22;
    const tMin = Math.min(...temps); const tMax = Math.max(...temps);
    // Degrés entiers strictement sous le minimum et au-dessus du maximum : la
    // courbe ne touche jamais l'axe des abscisses ni le bord du haut.
    const yMin = Math.ceil(tMin / 10) * 10 - 10;
    const yMax = Math.floor(tMax / 10) * 10 + 10;
    const xMin = HOURS[0]; const xMax = HOURS[HOURS.length - 1];
    const x0 = mL; const y0 = h - mB;
    const X = (hr) => x0 + ((hr - xMin) / (xMax - xMin)) * (w - mL - mR);
    const Y = (t10) => y0 - ((t10 - yMin) / (yMax - yMin)) * (y0 - mT);

    let grille = '';
    for (let t = yMin; t <= yMax; t += 5) {           // une ligne tous les 0,5 °C
        const entier = t % 10 === 0;
        grille += `<line x1="${x0}" y1="${Y(t).toFixed(1)}" x2="${w - mR}" y2="${Y(t).toFixed(1)}" stroke="${entier ? '#cbd5e1' : '#e2e8f0'}" stroke-width="1"/>`;
        if (entier) grille += `<text x="${x0 - 6}" y="${(Y(t) + 4).toFixed(1)}" text-anchor="end" font-size="11" fill="#475569">${t / 10}</text>`;
    }
    HOURS.forEach((hr) => {
        grille += `<line x1="${X(hr).toFixed(1)}" y1="${mT}" x2="${X(hr).toFixed(1)}" y2="${y0}" stroke="#e2e8f0" stroke-width="1"/>`;
        grille += `<text x="${X(hr).toFixed(1)}" y="${y0 + 16}" text-anchor="middle" font-size="11" fill="#475569">${hr}</text>`;
    });

    const pts = HOURS.map((hr, i) => `${X(hr).toFixed(1)},${Y(temps[i]).toFixed(1)}`).join(' ');
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}" width="${w}" height="${h}" font-family="Arial">`
        + grille
        + `<line x1="${x0}" y1="${y0}" x2="${w - 6}" y2="${y0}" stroke="#1e293b" stroke-width="1.5"/>`
        + `<line x1="${x0}" y1="${y0}" x2="${x0}" y2="8" stroke="#1e293b" stroke-width="1.5"/>`
        + `<polygon points="${w - 4},${y0} ${w - 12},${y0 - 4} ${w - 12},${y0 + 4}" fill="#1e293b"/>`
        + `<polygon points="${x0},6 ${x0 - 4},14 ${x0 + 4},14" fill="#1e293b"/>`
        + `<text x="${x0 + 10}" y="18" text-anchor="start" font-size="11" fill="#1e293b">Température (en °C)</text>`
        + `<text x="${w - mR}" y="${y0 + 34}" text-anchor="end" font-size="11" fill="#1e293b">Heure (en h)</text>`
        + `<polyline points="${pts}" fill="none" stroke="#DC2626" stroke-width="2.5" stroke-linejoin="round"/>`
        + '</svg>';
};
